import { API_URL } from '../config/api';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import VideoCard from './VideoCard';
import './CreatorDetail.css';
import { CREATOR_STATUS_OPTIONS, LEGACY_STATUS_OPTIONS, getStatusColor } from '../constants/creatorStatus';
import { extractTiktokVideoId } from '../utils/postUrlHelper';

interface Post {
  postUrl: string;  // PRIMARY KEY
  thumbnailUrl: string;
  viewCount?: number;
  likeCount?: number;
  commentCount?: number;
  shareCount?: number;
  saveCount?: number;
  downloadCount?: number;
  videoUrl?: string;
  duration?: number;
  uploadDate?: string;
  isDiscoveryVideo?: boolean;
  desc?: string;
}

interface Account {
  _id: string;
  username: string;
  email?: string;
  keyword?: string;
  bio?: string;
  relevancy?: boolean;
  relevancyReason?: string;
  relevantKeywords?: Record<string, string[]>;
  averageViews?: number;
  medianViews?: number;
  status?: string;
  createdAt: string;
  updatedAt: string;
  emailSentAt?: string;
  platform?: 'instagram' | 'tiktok';
  fetchedAt?: string;
  userId?: string;
  discoveryPostUrl?: string;
  discoveryVideo?: Post;
}

// type TabType = 'overview' | 'posts' | 'analytics' | 'history'; // Posts 데이터 없이 페이지 구성

// Helper function to get category-specific colors
const getCategoryColor = (category: string) => {
  switch (category.toLowerCase()) {
    case 'aitools':
      return {
        bg: '#fef3c7', // yellow-100
        text: '#92400e', // yellow-800
        keywordBg: '#fffbeb', // yellow-50
        keywordText: '#92400e',
        border: '#fcd34d' // yellow-300
      };
    case 'app':
      return {
        bg: '#dbeafe', // blue-100
        text: '#1e40af', // blue-800
        keywordBg: '#eff6ff', // blue-50
        keywordText: '#1e40af',
        border: '#60a5fa' // blue-300
      };
    case 'software':
      return {
        bg: '#e5e7eb', // gray-200
        text: '#374151', // gray-700
        keywordBg: '#f9fafb', // gray-50
        keywordText: '#374151',
        border: '#9ca3af' // gray-400
      };
    case 'sidehustle':
      return {
        bg: '#dcfce7', // green-100
        text: '#166534', // green-800
        keywordBg: '#f0fdf4', // green-50
        keywordText: '#166534',
        border: '#86efac' // green-300
      };
    default:
      return {
        bg: '#f3e8ff', // purple-100
        text: '#7c3aed', // purple-600
        keywordBg: '#faf5ff', // purple-50
        keywordText: '#7c3aed',
        border: '#c4b5fd' // purple-300
      };
  }
};

const CreatorDetail: React.FC = () => {
  const { platform, username } = useParams<{ platform: string; username: string }>();
  const navigate = useNavigate();
  
  const [creator, setCreator] = useState<Account | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [postsPagination, setPostsPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  // Navigation state
  const [allCreators, setAllCreators] = useState<Account[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    email: '',
    keyword: '',
    bio: '',
    relevancy: false,
    relevancyReason: '',
    averageViews: '',
    medianViews: '',
    status: ''
  });
  
  // Transcript modal state
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [transcriptData, setTranscriptData] = useState<any>(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);
  const [transcriptVideoUrl, setTranscriptVideoUrl] = useState<string>('');

  // Relevancy analysis modal state
  const [showRelevancyModal, setShowRelevancyModal] = useState(false);
  const [relevancyData, setRelevancyData] = useState<any>(null);
  const [relevancyLoading, setRelevancyLoading] = useState(false);
  const [relevancyError, setRelevancyError] = useState<string | null>(null);

  // Format number
  const formatNumber = (num?: number) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  // Format date
  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format date relative - 현재 사용하지 않음 (Posts 데이터 없이 페이지 구성)
  // const formatDateRelative = (date: string) => {
  //   if (!date) return '-';
  //   const now = new Date();
  //   const then = new Date(date);
  //   const diff = now.getTime() - then.getTime();
  //   const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
  //   if (days === 0) return 'Today';
  //   if (days === 1) return 'Yesterday';
  //   if (days < 7) return `${days} days ago`;
  //   if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  //   if (days < 365) return `${Math.floor(days / 30)} months ago`;
  //   return `${Math.floor(days / 365)} years ago`;
  // };

  // Calculate engagement rate
  const calculateEngagementRate = () => {
    if (!posts || posts.length === 0) return 0;
    const totalViews = posts.reduce((sum, post) => sum + (post.viewCount || 0), 0);
    const totalEngagement = posts.reduce((sum, post) => 
      sum + (post.likeCount || 0) + (post.commentCount || 0) + (post.shareCount || 0), 0
    );
    if (totalViews === 0) return 0;
    return ((totalEngagement / totalViews) * 100).toFixed(2);
  };

  // Calculate average metrics
  const calculateAverageMetrics = () => {
    if (!posts || posts.length === 0) return { views: 0, likes: 0, comments: 0, shares: 0 };
    const count = posts.length;
    return {
      views: Math.round(posts.reduce((sum, post) => sum + (post.viewCount || 0), 0) / count),
      likes: Math.round(posts.reduce((sum, post) => sum + (post.likeCount || 0), 0) / count),
      comments: Math.round(posts.reduce((sum, post) => sum + (post.commentCount || 0), 0) / count),
      shares: Math.round(posts.reduce((sum, post) => sum + (post.shareCount || 0), 0) / count)
    };
  };

  // Get best performing post
  const getBestPost = () => {
    if (!posts || posts.length === 0) return null;
    return posts.reduce((best, post) => 
      (post.viewCount || 0) > (best.viewCount || 0) ? post : best
    );
  };

  // Fetch all creators for navigation
  const fetchAllCreators = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/creators/${platform}?limit=1000`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setAllCreators(data.data);
          const index = data.data.findIndex((c: Account) => c.username === username);
          setCurrentIndex(index);
        }
      }
    } catch (error) {
      console.error('Error fetching all creators:', error);
    }
  };

  // Navigate to creator by index
  const navigateToCreator = (index: number) => {
    if (index >= 0 && index < allCreators.length) {
      const targetCreator = allCreators[index];
      navigate(`/creators/${platform}/${targetCreator.username}`);
    }
  };

  // Fetch creator details
  const fetchCreatorDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/creators/${platform}/${username}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch creator details');
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        setCreator(data.data);
        setEditForm({
          email: data.data.email || '',
          keyword: data.data.keyword || '',
          bio: data.data.bio || '',
          relevancy: data.data.relevancy || false,
          relevancyReason: data.data.relevancyReason || '',
          averageViews: data.data.averageViews ? data.data.averageViews.toString() : '',
          medianViews: data.data.medianViews ? data.data.medianViews.toString() : '',
          status: data.data.status || ''
        });
      } else {
        setError('Creator not found');
      }
    } catch (error) {
      console.error('Error fetching creator:', error);
      setError('Failed to fetch creator details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch posts
  const fetchPosts = async (page: number = 1) => {
    if (!platform || !username) return;
    
    setPostsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/posts/${platform}/${username}?page=${page}&limit=50`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch posts');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setPosts(data.data);
        setPostsPagination(data.pagination);
      } else {
        setError(data.error || 'Failed to fetch posts');
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setError('Failed to fetch posts');
    } finally {
      setPostsLoading(false);
    }
  };

  // Update creator
  const updateCreator = async () => {
    if (!creator) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/creators/${platform}/${creator._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: editForm.email || undefined,
            keyword: editForm.keyword || undefined,
            bio: editForm.bio || undefined,
            relevancy: editForm.relevancy,
            relevancyReason: editForm.relevancyReason || undefined,
            averageViews: editForm.averageViews ? parseInt(editForm.averageViews) : undefined,
            medianViews: editForm.medianViews ? parseInt(editForm.medianViews) : undefined,
            status: editForm.status || undefined
          })
        }
      );

      if (response.ok) {
        setShowEditModal(false);
        fetchCreatorDetails();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update creator');
      }
    } catch (error) {
      setError('Failed to update creator');
      console.error('Error updating creator:', error);
    }
  };

  // Fetch transcript
  const fetchTranscript = async (videoUrl: string) => {
    setShowTranscriptModal(true);
    setTranscriptLoading(true);
    setTranscriptError(null);
    setTranscriptVideoUrl(videoUrl);
    
    try {
      const response = await fetch(
        `${API_URL}/api/transcript?url=${encodeURIComponent(videoUrl)}&text=true`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch transcript');
      }
      
      const data = await response.json();
      setTranscriptData(data);
    } catch (error) {
      console.error('Error fetching transcript:', error);
      setTranscriptError(error instanceof Error ? error.message : 'Failed to fetch transcript');
    } finally {
      setTranscriptLoading(false);
    }
  };

  // Fetch relevancy analysis
  const fetchRelevancyAnalysis = async () => {
    setShowRelevancyModal(true);
    setRelevancyLoading(true);
    setRelevancyError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/relevance/analyze-creator`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            handle: username,
            platform: platform
          })
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch relevancy analysis');
      }
      
      const data = await response.json();
      setRelevancyData(data);
    } catch (error) {
      console.error('Error fetching relevancy analysis:', error);
      setRelevancyError(error instanceof Error ? error.message : 'Failed to fetch relevancy analysis');
    } finally {
      setRelevancyLoading(false);
    }
  };

  // Delete creator
  const deleteCreator = async () => {
    if (!creator || !window.confirm(`Are you sure you want to delete @${creator.username}?`)) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/creators/${platform}/${creator._id}`, 
        { method: 'DELETE' }
      );

      if (response.ok) {
        navigate('/creators');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete creator');
      }
    } catch (error) {
      setError('Failed to delete creator');
      console.error('Error deleting creator:', error);
    }
  };

  useEffect(() => {
    if (platform && username) {
      fetchCreatorDetails();
      // fetchPosts(1); // Posts 데이터 없이 페이지 구성
      fetchAllCreators();
    }
  }, [platform, username]);

  // Load TikTok embed script when creator has discovery post
  useEffect(() => {
    if (creator?.discoveryPostUrl) {
      // Check if TikTok embed script is already loaded
      if (!document.querySelector('script[src="https://www.tiktok.com/embed.js"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.tiktok.com/embed.js';
        script.async = true;
        document.body.appendChild(script);
      } else {
        // If script is already loaded, re-initialize TikTok embeds
        if ((window as any).tiktok) {
          (window as any).tiktok.init();
        }
      }
    }
  }, [creator?.discoveryPostUrl]);

  if (loading) {
    return (
      <div className="tab-content">
        <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
          <div className="loading">Loading creator details...</div>
        </div>
      </div>
    );
  }

  if (error && !creator) {
    return (
      <div className="tab-content">
        <div className="text-center" style={{ padding: 'var(--space-16)' }}>
          <div className="error mb-4">❌ {error}</div>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/creators')}
          >
            ← Back to Creators
          </button>
        </div>
      </div>
    );
  }

  // Posts 데이터 없이 페이지 구성 - 아래 변수들은 사용하지 않음
  // const avgMetrics = calculateAverageMetrics();
  // const bestPost = getBestPost();

  return (
    <div className="tab-content">
      {/* Header Section */}
      <div 
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-8)',
          marginBottom: 'var(--space-6)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          transform: 'translate(100px, -100px)',
          pointerEvents: 'none'
        }} />
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-4)'
        }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              className="btn"
              onClick={() => navigate('/creators')}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white'
              }}
            >
              ← Back to Creators
            </button>
            <button
              className="btn"
              onClick={() => navigate(`/outreach/responses?search=${username}`)}
              style={{
                background: 'rgba(99, 102, 241, 0.3)',
                border: '1px solid rgba(99, 102, 241, 0.5)',
                color: 'white'
              }}
            >
              📧 View Responses
            </button>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              className="btn"
              onClick={() => navigateToCreator(currentIndex - 1)}
              disabled={currentIndex <= 0}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                opacity: currentIndex <= 0 ? 0.5 : 1,
                cursor: currentIndex <= 0 ? 'not-allowed' : 'pointer'
              }}
            >
              ← Previous
            </button>
            <button
              className="btn"
              onClick={() => navigateToCreator(currentIndex + 1)}
              disabled={currentIndex >= allCreators.length - 1}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                opacity: currentIndex >= allCreators.length - 1 ? 0.5 : 1,
                cursor: currentIndex >= allCreators.length - 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Next →
            </button>
          </div>
        </div>
        
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-4xl font-bold" style={{ margin: 0 }}>
              @{username}
            </h1>
            {creator?.keyword && (
              <span style={{ 
                color: 'white',
                opacity: 0.9,
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                🏷️ {creator.keyword}
              </span>
            )}
            {creator?.status && (
              <span 
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  background: creator.status === 'active' ? 'rgba(82, 255, 168, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                  border: `1px solid ${creator.status === 'active' ? 'rgba(82, 255, 168, 0.5)' : 'rgba(255, 255, 255, 0.3)'}`
                }}
              >
                📊 {creator.status}
              </span>
            )}
            {creator?.relevancy !== undefined && (
              <span style={{ 
                color: 'white',
                opacity: 0.9,
                fontSize: '14px'
              }}>
                {creator.relevancy ? '✅ Relevant' : '⚠️ Not Relevant'}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3 mt-3">
            <a
              href={`https://${platform === 'instagram' 
                ? `instagram.com/${username}` 
                : `tiktok.com/@${username}`}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn"
              style={{
                background: 'white',
                color: '#667eea',
                padding: '6px 16px',
                fontSize: '14px'
              }}
            >
              {platform === 'instagram' ? 'View in Instagram' : 'View in TikTok'} ↗
            </a>
            <button 
              className="btn"
              onClick={() => setShowEditModal(true)}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                padding: '6px 16px',
                fontSize: '14px'
              }}
            >
              ✏️ Edit
            </button>
            <button 
              className="btn"
              onClick={fetchRelevancyAnalysis}
              style={{
                background: 'rgba(34, 197, 94, 0.2)',
                border: '1px solid rgba(34, 197, 94, 0.5)',
                color: 'white',
                padding: '6px 16px',
                fontSize: '14px'
              }}
            >
              🎯 Analyze Relevancy
            </button>
            <button 
              className="btn"
              onClick={deleteCreator}
              style={{
                background: 'rgba(255, 107, 107, 0.2)',
                border: '1px solid rgba(255, 107, 107, 0.5)',
                color: 'white',
                padding: '6px 16px',
                fontSize: '14px'
              }}
            >
              🗑️ Delete
            </button>
          </div>
          
          {creator?.bio && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <p style={{ opacity: 0.7, fontSize: '0.875rem', marginBottom: '0.25rem' }}>Bio:</p>
              <p style={{ opacity: 0.9 }}>
                {creator.bio}
              </p>
            </div>
          )}
          
          <div style={{ marginTop: 'var(--space-3)', padding: '0.75rem', backgroundColor: 'var(--background-subtle)', borderRadius: 'var(--radius-2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>Relevancy:</span>
              <span style={{ 
                padding: '0.125rem 0.5rem', 
                borderRadius: 'var(--radius-1)',
                fontSize: '0.75rem',
                fontWeight: 500,
                backgroundColor: creator?.relevancy ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                color: creator?.relevancy ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'
              }}>
                {creator?.relevancy ? 'Relevant' : 'Not Relevant'}
              </span>
            </div>
            {creator?.relevancyReason && (
              <p style={{ fontSize: '0.875rem', opacity: 0.8, lineHeight: 1.5, marginBottom: '0.75rem' }}>
                {creator.relevancyReason}
              </p>
            )}
            {creator?.relevancy && creator?.relevantKeywords && Object.keys(creator.relevantKeywords).length > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <p style={{ fontSize: '0.875rem', opacity: 0.7, marginBottom: '0.5rem' }}>Related Keywords:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {Object.entries(creator.relevantKeywords).map(([category, keywords]) => {
                    // Ensure keywords is an array
                    const keywordArray = Array.isArray(keywords) ? keywords : [];
                    return keywordArray.length > 0 && (
                      <div key={category} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center' }}>
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          padding: '0.25rem 0.5rem',
                          backgroundColor: getCategoryColor(category).bg,
                          color: getCategoryColor(category).text,
                          borderRadius: '0.375rem',
                          marginRight: '0.5rem',
                          textTransform: 'uppercase'
                        }}>
                          {category}
                        </span>
                        {keywordArray.map((keyword, idx) => (
                          <span
                            key={idx}
                            style={{
                              fontSize: '0.75rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: getCategoryColor(category).keywordBg,
                              color: getCategoryColor(category).keywordText,
                              borderRadius: '0.375rem',
                              border: `1px solid ${getCategoryColor(category).border}`
                            }}
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {creator?.email && (
            <div style={{ opacity: 0.8, marginTop: 'var(--space-2)', fontSize: '14px' }}>
              📧 {creator.email}
            </div>
          )}
        </div>
      </div>


      {/* All Content Sections */}
      <div className="space-y-8">
        {/* Overview Section */}
        <div>
          <h2 className="text-2xl font-bold mb-4" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            📊 Overview
          </h2>
          <div className="grid gap-6">
          {/* Quick Stats - Med Views만 표시 (Posts 데이터 없이) */}
          <div className="card">
            <h3 className="text-xl font-semibold mb-4">Performance Metrics</h3>
            <div 
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 'var(--space-4)'
              }}
            >
              <div className="metric-box">
                <div className="metric-label">Median Views</div>
                <div className="metric-value">{formatNumber(creator?.medianViews)}</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  Median view count across all posts
                </div>
              </div>
              {creator?.averageViews && (
              <div className="metric-box">
                <div className="metric-label">Average Views</div>
                <div className="metric-value">{formatNumber(creator?.averageViews)}</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  Average view count across all posts
                </div>
              </div>
              )}
            </div>
          </div>


          {/* Account Information */}
          <div className="card">
            <h3 className="text-xl font-semibold mb-4">Account Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Username</span>
                <span className="info-value">@{username}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Platform</span>
                <span className="info-value">{platform === 'instagram' ? 'Instagram' : 'TikTok'}</span>
              </div>
              {creator?.email && (
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{creator.email}</span>
                </div>
              )}
              {creator?.keyword && (
                <div className="info-item">
                  <span className="info-label">Keyword</span>
                  <span className="info-value">{creator.keyword}</span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Status</span>
                <span 
                  className="info-value" 
                  style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    ...(creator?.status ? getStatusColor(creator.status) : {
                      backgroundColor: '#f0f0f0',
                      color: '#666666'
                    })
                  }}
                >
                  {creator?.status || 'Not Set'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Relevancy</span>
                <span className="info-value">{creator?.relevancy ? 'Yes' : 'No'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Created</span>
                <span className="info-value">{creator ? formatDate(creator.createdAt) : '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Updated</span>
                <span className="info-value">{creator ? formatDate(creator.updatedAt) : '-'}</span>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Discovery Video Section */}
        {creator?.discoveryPostUrl && (
          <div style={{ marginBottom: 'var(--space-8)' }}>
            <h2 className="text-2xl font-bold mb-4" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              ⭐ Discovery Video: {creator.keyword || 'N/A'}
            </h2>
            <div style={{
              padding: '16px',
              backgroundColor: '#fffbeb',
              border: '2px solid #fcd34d',
              borderRadius: '12px',
              marginBottom: '16px'
            }}>
              {/* TikTok Embed */}
              {(() => {
                const discoveryUrl = creator.discoveryPostUrl;
                const videoId = extractTiktokVideoId(creator.discoveryPostUrl);
                
                return (
                  <blockquote 
                    className="tiktok-embed" 
                    cite={discoveryUrl}
                    data-video-id={videoId}
                    style={{ maxWidth: '605px', minWidth: '325px' }}
                  >
                    <section>
                      <a 
                        target="_blank" 
                        rel="noopener noreferrer"
                        title={`@${creator.username}`}
                        href={discoveryUrl}
                      >
                        View on TikTok
                      </a>
                    </section>
                  </blockquote>
                );
              })()}
              
              <p style={{ margin: '16px 0 0 0', color: '#92400e', fontSize: '12px' }}>
                This post was used to discover this creator. Click above to view on TikTok.
              </p>
            </div>
          </div>
        )}

        {/* Posts Section - Hidden (Posts 데이터 없이 페이지 구성) */}
        {false && (
        <div>
          <h2 className="text-2xl font-bold mb-4" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            📹 Posts (30d)
          </h2>
        <div>
          {postsLoading && (
            <div className="loading">Loading posts...</div>
          )}

          {!postsLoading && posts.length === 0 && (
            <div className="card text-center">
              <p className="text-secondary">No posts found for this creator</p>
            </div>
          )}

          {!postsLoading && posts.length > 0 && (
            <>
              <div 
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '20px',
                  marginBottom: 'var(--space-6)'
                }}
              >
                {posts.map((post) => (
                  <VideoCard
                    key={post.postUrl}
                    thumbnailUrl={post.thumbnailUrl}
                    postUrl={post.postUrl}
                    viewCount={post.viewCount}
                    likeCount={post.likeCount}
                    commentCount={post.commentCount}
                    shareCount={post.shareCount}
                    saveCount={post.saveCount}
                    duration={post.duration}
                    uploadDate={post.uploadDate}
                    description={post.desc}
                    platform={platform as 'instagram' | 'tiktok'}
                    onTranscriptClick={fetchTranscript}
                    isDiscoveryVideo={post.isDiscoveryVideo || post.postUrl === creator?.discoveryPostUrl}
                  />
                ))}
              </div>

              {postsPagination.totalPages > 1 && (
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => fetchPosts(postsPagination.page - 1)}
                    disabled={postsPagination.page === 1}
                    className="btn btn-secondary"
                  >
                    Previous
                  </button>
                  <span className="flex items-center px-4">
                    Page {postsPagination.page} of {postsPagination.totalPages}
                  </span>
                  <button
                    onClick={() => fetchPosts(postsPagination.page + 1)}
                    disabled={postsPagination.page === postsPagination.totalPages}
                    className="btn btn-secondary"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        </div>
        )}

        {/* Analytics Section - Hidden (Posts 데이터 없이 페이지 구성) */}
        {false && (
        <div>
          <h2 className="text-2xl font-bold mb-4" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            📈 Analytics
          </h2>
        <div className="grid gap-6">
          <div className="card">
            <h3 className="text-xl font-semibold mb-4">Engagement Analysis</h3>
            <div className="analytics-grid">
              <div className="analytics-item">
                <div className="analytics-label">Engagement Rate</div>
                <div className="analytics-value text-2xl font-bold">
                  {calculateEngagementRate()}%
                </div>
                <div className="analytics-desc">
                  Based on likes, comments, and shares relative to views
                </div>
              </div>
              <div className="analytics-item">
                <div className="analytics-label">Best Performing Content</div>
                {/* bestPost 변수 제거됨 - Posts 데이터 없이 구성 */}
                <div className="analytics-value">No data</div>
              </div>
              <div className="analytics-item">
                <div className="analytics-label">Sample Size</div>
                <div className="analytics-value">
                  {posts.length > 0 ? `${posts.length} posts tracked` : 'No posts'}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-xl font-semibold mb-4">Performance Distribution</h3>
            <div className="performance-bars">
              {posts.slice(0, 10).map((post, index) => {
                const postDate = post.uploadDate 
                  ? new Date(post.uploadDate).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: 'numeric'
                    })
                  : `Post ${index + 1}`;
                return (
                  <div key={post.postUrl} className="performance-bar">
                    <div className="bar-label">{postDate}</div>
                    <div className="bar-container">
                      <div 
                        className="bar-fill"
                        style={{
                          width: `${(post.viewCount || 0) / Math.max(...posts.map(p => p.viewCount || 0)) * 100}%`,
                          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                        }}
                      />
                    </div>
                    <div className="bar-value">{formatNumber(post.viewCount)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        </div>
        )}

        {/* History Section */}
        <div>
          <h2 className="text-2xl font-bold mb-4" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            🕐 History
          </h2>
        <div className="card">
          <h3 className="text-xl font-semibold mb-4">Activity Timeline</h3>
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-marker" />
              <div className="timeline-content">
                <div className="timeline-date">{creator ? formatDate(creator.createdAt) : '-'}</div>
                <div className="timeline-title">Account Added</div>
                <div className="timeline-desc">Creator profile was added to the system</div>
              </div>
            </div>
            {creator?.emailSentAt && (
              <div className="timeline-item">
                <div className="timeline-marker" />
                <div className="timeline-content">
                  <div className="timeline-date">{formatDate(creator.emailSentAt)}</div>
                  <div className="timeline-title">Email Sent</div>
                  <div className="timeline-desc">Outreach email was sent to creator</div>
                </div>
              </div>
            )}
            {creator?.fetchedAt && (
              <div className="timeline-item">
                <div className="timeline-marker" />
                <div className="timeline-content">
                  <div className="timeline-date">{formatDate(creator.fetchedAt)}</div>
                  <div className="timeline-title">Data Fetched</div>
                  <div className="timeline-desc">Latest posts and metrics were fetched</div>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="text-2xl font-bold">Edit Creator</h2>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  className="input"
                  placeholder="Enter email address"
                />
              </div>
              
              <div className="form-group">
                <label>Keyword</label>
                <input
                  type="text"
                  value={editForm.keyword}
                  onChange={(e) => setEditForm(prev => ({ ...prev, keyword: e.target.value }))}
                  className="input"
                  placeholder="Enter keyword"
                />
              </div>
              
              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                  className="input"
                  rows={3}
                  placeholder="Enter bio"
                />
              </div>
              
              <div className="form-group">
                <label>Relevancy Reason</label>
                <textarea
                  value={editForm.relevancyReason}
                  onChange={(e) => setEditForm(prev => ({ ...prev, relevancyReason: e.target.value }))}
                  className="input"
                  rows={3}
                  placeholder="Enter relevancy reason"
                />
              </div>
              
              <div className="form-group">
                <label>Average Views</label>
                <input
                  type="number"
                  value={editForm.averageViews}
                  onChange={(e) => setEditForm(prev => ({ ...prev, averageViews: e.target.value }))}
                  className="input"
                  placeholder="Enter average views"
                />
              </div>
              
              <div className="form-group">
                <label>Median Views</label>
                <input
                  type="number"
                  value={editForm.medianViews}
                  onChange={(e) => setEditForm(prev => ({ ...prev, medianViews: e.target.value }))}
                  className="input"
                  placeholder="Enter median views"
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                  className="input"
                >
                  <option value="">Select status</option>
                  {CREATOR_STATUS_OPTIONS.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                  {/* Legacy options for backward compatibility */}
                  {LEGACY_STATUS_OPTIONS.slice(1).map(status => (
                    <option key={`legacy-${status.value}`} value={status.value}>
                      {status.label} (Legacy)
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editForm.relevancy}
                    onChange={(e) => setEditForm(prev => ({ ...prev, relevancy: e.target.checked }))}
                  />
                  <span>Is Relevant</span>
                </label>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-primary"
                onClick={updateCreator}
              >
                Save Changes
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Transcript Modal - Floating */}
      {showTranscriptModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px',
            backdropFilter: 'blur(5px)'
          }}
          onClick={() => {
            setShowTranscriptModal(false);
            setTranscriptData(null);
            setTranscriptError(null);
          }}
        >
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#2a2a2a',
              borderRadius: '12px',
              maxWidth: '800px',
              maxHeight: '90vh',
              width: '90%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '20px',
                borderBottom: '1px solid #444',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ margin: 0, color: '#fff', fontSize: '20px' }}>📝 Video Transcript</h3>
              <button
                onClick={() => {
                  setShowTranscriptModal(false);
                  setTranscriptData(null);
                  setTranscriptError(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#999',
                  fontSize: '28px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '35px',
                  height: '35px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            
            {/* Modal Content */}
            <div
              style={{
                padding: '30px',
                overflowY: 'auto',
                flex: 1,
                fontSize: '16px'
              }}
            >
              {transcriptLoading && (
                <div style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
                  <div style={{ fontSize: '20px', marginBottom: '10px' }}>⏳</div>
                  Loading transcript...
                </div>
              )}
              
              {transcriptError && (
                <div style={{ color: '#ff6b6b', textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px' }}>❌</div>
                  {transcriptError}
                </div>
              )}
              
              {transcriptData && !transcriptLoading && (
                <div>
                  {transcriptData.lang && (
                    <div
                      style={{
                        marginBottom: '20px',
                        padding: '12px',
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        borderRadius: '8px',
                        color: '#999',
                        fontSize: '14px'
                      }}
                    >
                      <strong>Language:</strong> {transcriptData.lang}
                      {transcriptData.availableLangs && transcriptData.availableLangs.length > 1 && (
                        <span style={{ marginLeft: '10px' }}>
                          (Available languages: {transcriptData.availableLangs.join(', ')})
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div
                    style={{
                      color: '#fff',
                      lineHeight: '1.8',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '15px'
                    }}
                  >
                    {typeof transcriptData.content === 'string'
                      ? transcriptData.content
                      : Array.isArray(transcriptData.content)
                      ? transcriptData.content.map((segment: any, index: number) => (
                          <div key={index} style={{ marginBottom: '12px' }}>
                            {segment.text}
                          </div>
                        ))
                      : 'No transcript available'}
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div
              style={{
                padding: '20px',
                borderTop: '1px solid #444',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}
            >
              <a
                href={transcriptVideoUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#444',
                  color: '#fff',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#555';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#444';
                }}
              >
                View Original Video →
              </a>
              <button
                onClick={() => {
                  setShowTranscriptModal(false);
                  setTranscriptData(null);
                  setTranscriptError(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#667eea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#764ba2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#667eea';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Relevancy Analysis Modal */}
      {showRelevancyModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px',
            backdropFilter: 'blur(5px)'
          }}
          onClick={() => {
            setShowRelevancyModal(false);
            setRelevancyData(null);
            setRelevancyError(null);
          }}
        >
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#2a2a2a',
              borderRadius: '12px',
              maxWidth: '900px',
              maxHeight: '90vh',
              width: '90%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '20px',
                borderBottom: '1px solid #444',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ margin: 0, color: '#fff', fontSize: '20px' }}>🎯 Relevancy Analysis Results</h3>
              <button
                onClick={() => {
                  setShowRelevancyModal(false);
                  setRelevancyData(null);
                  setRelevancyError(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#999',
                  fontSize: '28px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '35px',
                  height: '35px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            
            {/* Modal Content */}
            <div
              style={{
                padding: '30px',
                overflowY: 'auto',
                flex: 1,
                fontSize: '16px'
              }}
            >
              {relevancyLoading && (
                <div style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
                  <div style={{ fontSize: '20px', marginBottom: '10px' }}>⏳</div>
                  Analyzing creator relevancy...
                </div>
              )}
              
              {relevancyError && (
                <div style={{ color: '#ff6b6b', textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px' }}>❌</div>
                  {relevancyError}
                </div>
              )}
              
              {relevancyData && !relevancyLoading && (
                <div>
                  {/* Main Result */}
                  <div style={{
                    marginBottom: '30px',
                    padding: '20px',
                    backgroundColor: relevancyData.isRelevant ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '12px',
                    border: `2px solid ${relevancyData.isRelevant ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '24px' }}>
                        {relevancyData.isRelevant ? '✅' : '❌'}
                      </span>
                      <h4 style={{ margin: 0, color: '#fff', fontSize: '18px' }}>
                        {relevancyData.isRelevant ? 'RELEVANT' : 'NOT RELEVANT'}
                      </h4>
                      {relevancyData.relevancyScore !== undefined && (
                        <span style={{
                          padding: '4px 12px',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '20px',
                          fontSize: '14px',
                          color: '#fff'
                        }}>
                          Score: {(relevancyData.relevancyScore * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Reasoning */}
                  {relevancyData.reasoning && (
                    <div style={{ marginBottom: '25px' }}>
                      <h5 style={{ color: '#fff', marginBottom: '10px', fontSize: '16px' }}>💭 Analysis Reasoning:</h5>
                      <p style={{
                        color: '#ccc',
                        lineHeight: '1.6',
                        padding: '15px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        margin: 0
                      }}>
                        {relevancyData.reasoning}
                      </p>
                    </div>
                  )}

                  {/* Relevant Topics */}
                  {relevancyData.relevantTopics && relevancyData.relevantTopics.length > 0 && (
                    <div style={{ marginBottom: '25px' }}>
                      <h5 style={{ color: '#fff', marginBottom: '12px', fontSize: '16px' }}>📋 Matching Topics:</h5>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {relevancyData.relevantTopics.map((topic: string, index: number) => (
                          <span key={index} style={{
                            padding: '6px 12px',
                            backgroundColor: 'rgba(34, 197, 94, 0.2)',
                            color: 'rgb(34, 197, 94)',
                            borderRadius: '20px',
                            fontSize: '14px',
                            border: '1px solid rgba(34, 197, 94, 0.3)'
                          }}>
                            {topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Relevant Keywords by Category */}
                  {relevancyData.relevantKeywords && Object.keys(relevancyData.relevantKeywords).length > 0 && (
                    <div style={{ marginBottom: '25px' }}>
                      <h5 style={{ color: '#fff', marginBottom: '15px', fontSize: '16px' }}>🔍 Found Keywords by Category:</h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {Object.entries(relevancyData.relevantKeywords).map(([category, keywords]) => {
                          const keywordArray = Array.isArray(keywords) ? keywords : [];
                          return keywordArray.length > 0 && (
                            <div key={category} style={{
                              padding: '15px',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '8px'
                            }}>
                              <div style={{ marginBottom: '10px' }}>
                                <span style={{
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  padding: '4px 12px',
                                  backgroundColor: getCategoryColor(category).bg,
                                  color: getCategoryColor(category).text,
                                  borderRadius: '6px',
                                  textTransform: 'uppercase'
                                }}>
                                  {category}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {keywordArray.map((keyword, idx) => (
                                  <span key={idx} style={{
                                    fontSize: '13px',
                                    padding: '4px 10px',
                                    backgroundColor: getCategoryColor(category).keywordBg,
                                    color: getCategoryColor(category).keywordText,
                                    borderRadius: '6px',
                                    border: `1px solid ${getCategoryColor(category).border}`
                                  }}>
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Analysis Stats */}
                  <div style={{
                    marginTop: '25px',
                    padding: '15px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px'
                  }}>
                    <h5 style={{ color: '#fff', marginBottom: '10px', fontSize: '16px' }}>📊 Analysis Information:</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                      <div>
                        <span style={{ color: '#999', fontSize: '14px' }}>Posts Analyzed:</span>
                        <span style={{ color: '#fff', marginLeft: '8px' }}>
                          {relevancyData.postsAnalyzed || 0}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#999', fontSize: '14px' }}>Platform:</span>
                        <span style={{ color: '#fff', marginLeft: '8px', textTransform: 'capitalize' }}>
                          {relevancyData.platform || platform}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div
              style={{
                padding: '20px',
                borderTop: '1px solid #444',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}
            >
              <button
                onClick={() => {
                  setShowRelevancyModal(false);
                  setRelevancyData(null);
                  setRelevancyError(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#667eea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#764ba2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#667eea';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorDetail;
